/**
 * react-query bindings for the API.
 *
 * Polling periods live in `src/config.ts`; every live query stops polling when
 * the tab is hidden (`useDocumentVisible`). Mutations invalidate exactly the
 * queries whose truth they changed — the UI never patches a cached object with
 * an optimistic guess, because secure storage and the server are the source of
 * truth and this browser is not.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import {
  ADMIN_POLL_MS,
  AUDIT_LIMIT,
  CATALOGUE_POLL_MS,
  CONFIG_STALE_MS,
  DATASET_ACTIVE_POLL_MS,
  DATASET_IDLE_POLL_MS,
} from "../config";
import { pollInterval, useDocumentVisible } from "../lib/visibility";
import { useApi, useSessionLost } from "./ApiProvider";
import { ApiError } from "./client";
import { DATASETS_KEY, queryKeys } from "./keys";
import type {
  Account,
  AccountCreate,
  AccountList,
  AccessGrant,
  AccessRequest,
  AdminUser,
  AdminUserList,
  AuditList,
  AuthConfig,
  ConsoleConfig,
  ConsoleKeyCreate,
  ConsoleKeyList,
  Consent,
  ConsentCreate,
  ConsentList,
  CreatedConsoleKey,
  Dataset,
  DatasetCreate,
  DatasetList,
  DatasetUpdate,
  DeletionRequest,
  DeletionRequestCreate,
  DisableRequest,
  Evaluation,
  EvaluationCreate,
  EvaluationList,
  FetchLines,
  Me,
  MeUpdate,
  PauseRequest,
  PauseState,
  Purpose,
  ReadList,
  SecondFactorCode,
  SecondFactorConfirmed,
  SecondFactorEnrolment,
  StorageHealth,
  Target,
  TargetCreate,
  TargetList,
  Version,
  VersionList,
} from "./types";
import { MOVING_VERSION_STATES } from "./types";

export { queryKeys };

/**
 * The single string extractor every screen uses to quote a failure.
 *
 * The server writes the sentence; this never rewrites one. A refusal message
 * names the rule and its number, and paraphrasing it here would put a second
 * copy of the rule in a place nobody would keep in step.
 *
 * Args:
 *     error: Anything thrown by a query or a mutation.
 *
 * Returns:
 *     One sentence to show.
 */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Something went wrong.";
}

// ---------------------------------------------------------------- sign-in

/** How the server signs people in; asked once, it does not change at runtime. */
export function useAuthConfig(): UseQueryResult<AuthConfig, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.authConfig,
    queryFn: ({ signal }) => api.getAuthConfig(signal),
    staleTime: Infinity,
  });
}

/**
 * Your account: identity, approval state and whether your code is set up.
 *
 * It waits for the published configuration and stops being asked the moment
 * the server refuses the session — there is nobody left to ask about. The wait
 * is not cosmetic: in the server's development mode the identity is a header
 * built from that configuration, and a `/me` sent before it arrived would be
 * anonymous, answered `401`, and would drop a laptop straight to a sign-in
 * screen it cannot use.
 */
export function useMe(): UseQueryResult<Me, Error> {
  const api = useApi();
  const sessionLost = useSessionLost();
  const config = useAuthConfig();
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => api.getMe(signal),
    enabled: !sessionLost && config.isSuccess,
    staleTime: 30_000,
  });
}

/** End the session on the server; the caller then reloads the application. */
export function useSignOut(): UseMutationResult<void, Error, void> {
  const api = useApi();
  return useMutation({ mutationFn: () => api.logout() });
}

/** Set the one field on this surface a person owns. */
export function useUpdateMe(): UseMutationResult<Me, Error, MeUpdate> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: MeUpdate) => api.updateMe(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

// ------------------------------------------------------------------ config

/**
 * Every bound, list and default a form would otherwise invent.
 *
 * Static for the life of a deployment, so it is cached rather than polled: a
 * form that re-asked for the metric table every ten seconds would be asking
 * the same question of the same release.
 */
export function useConsoleConfig(): UseQueryResult<ConsoleConfig, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.config,
    queryFn: ({ signal }) => api.getConfig(signal),
    staleTime: CONFIG_STALE_MS,
  });
}

// ---------------------------------------------------------- second factor

/** Start setting up your code. The answer carries the secret exactly once. */
export function useEnrolSecondFactor(): UseMutationResult<
  SecondFactorEnrolment,
  Error,
  void
> {
  const api = useApi();
  return useMutation({ mutationFn: () => api.enrolSecondFactor() });
}

/**
 * Confirm the enrolment, or open the step-up window before a gated act.
 *
 * One route, two jobs, and it is one route on purpose: a caller that has just
 * been refused with `step_up_required` calls this and retries the act.
 */
export function useVerifySecondFactor(): UseMutationResult<
  SecondFactorConfirmed,
  Error,
  SecondFactorCode
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SecondFactorCode) => api.verifySecondFactor(payload),
    onSuccess: () => {
      // `/me` publishes when the window opened and when it shuts, and screens
      // say "you will be asked for your code" from it.
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

/** Remove your code; a current or recovery code is required in the body. */
export function useRemoveSecondFactor(): UseMutationResult<
  void,
  Error,
  SecondFactorCode
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SecondFactorCode) => api.removeSecondFactor(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

// ------------------------------------------------------------ console keys

/** The keys that can reach the catalogue right now, newest first. */
export function useConsoleKeys(): UseQueryResult<ConsoleKeyList, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.consoleKeys,
    queryFn: ({ signal }) => api.listConsoleKeys(signal),
  });
}

/** Mint a key. Asks for your code, and shows the secret once. */
export function useCreateConsoleKey(): UseMutationResult<
  CreatedConsoleKey,
  Error,
  ConsoleKeyCreate
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ConsoleKeyCreate) => api.createConsoleKey(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.consoleKeys });
    },
  });
}

/** Revoke one of your own keys; it is dead at its next request. */
export function useRevokeConsoleKey(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.revokeConsoleKey(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.consoleKeys });
    },
  });
}

// ---------------------------------------------------------------- accounts

/** The client companies we hold data for, by name. */
export function useAccounts(): UseQueryResult<AccountList, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.accounts,
    queryFn: ({ signal }) => api.listAccounts(undefined, signal),
    refetchInterval: pollInterval(CATALOGUE_POLL_MS, visible),
  });
}

/** Add a client company. Its short name is frozen from that moment. */
export function useCreateAccount(): UseMutationResult<
  Account,
  Error,
  AccountCreate
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AccountCreate) => api.createAccount(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
    },
  });
}

// ---------------------------------------------------------------- datasets

/** The catalogue, newest first, optionally narrowed to one client. */
export function useDatasets(
  account?: string,
): UseQueryResult<DatasetList, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.datasets(account),
    queryFn: ({ signal }) => api.listDatasets({ account }, signal),
    refetchInterval: pollInterval(CATALOGUE_POLL_MS, visible),
  });
}

/**
 * One dataset, polled faster while a file of its own is still moving.
 *
 * "Moving" is the version states the console leaves on its own — the bytes on
 * their way, and the pass that reads them. Everything else has settled, and a
 * settled dataset changes when a person changes it.
 */
export function useDataset(id: string): UseQueryResult<Dataset, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.dataset(id),
    queryFn: ({ signal }) => api.getDataset(id, undefined, signal),
    refetchInterval: (query) => {
      const latest = query.state.data?.versions.latest;
      const period =
        latest && MOVING_VERSION_STATES.includes(latest.state)
          ? DATASET_ACTIVE_POLL_MS
          : DATASET_IDLE_POLL_MS;
      return pollInterval(period, visible);
    },
  });
}

/** Record a dataset, its first consent and — when there is one — its first bar. */
export function useCreateDataset(): UseMutationResult<
  Dataset,
  Error,
  DatasetCreate
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: DatasetCreate) => api.createDataset(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DATASETS_KEY });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
    },
  });
}

/** Correct a declared field. The correction is written into the trace. */
export function useUpdateDataset(
  id: string,
): UseMutationResult<Dataset, Error, DatasetUpdate> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: DatasetUpdate) => api.updateDataset(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataset(id) });
    },
  });
}

/** Every version of a dataset, newest first. */
export function useVersions(id: string): UseQueryResult<VersionList, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.datasetVersions(id),
    queryFn: ({ signal }) => api.listVersions(id, signal),
    refetchInterval: (query) => {
      const moving = (query.state.data?.items ?? []).some((version: Version) =>
        MOVING_VERSION_STATES.includes(version.state),
      );
      return pollInterval(
        moving ? DATASET_ACTIVE_POLL_MS : DATASET_IDLE_POLL_MS,
        visible,
      );
    },
  });
}

/**
 * The console's own copy-paste lines for a version.
 *
 * Only asked for once a version is available: there is no block to print for a
 * file that is not there, and asking anyway would answer `409` with a sentence
 * the version card already shows.
 */
export function useFetchLines(
  id: string,
  number: number,
  purpose: Purpose | undefined,
  enabled: boolean,
): UseQueryResult<FetchLines, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fetchLines(id, number, purpose ?? "server-default"),
    queryFn: ({ signal }) => api.getFetchLines(id, number, purpose, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** The whole consent ledger, newest first, superseded rows included. */
export function useConsentLedger(
  id: string,
): UseQueryResult<ConsentList, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.datasetConsent(id),
    queryFn: ({ signal }) => api.listConsent(id, signal),
  });
}

/**
 * Record the whole new permission list.
 *
 * Narrowing is immediate and asks for nothing; widening answers
 * `403 step_up_required` until a code has been confirmed, and the screen
 * replays the same request afterwards.
 */
export function useRecordConsent(
  id: string,
): UseMutationResult<Consent, Error, ConsentCreate> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ConsentCreate) => api.recordConsent(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataset(id) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.datasetConsent(id),
      });
    },
  });
}

/** The bar's own history: it moves during a negotiation. */
export function useTargets(id: string): UseQueryResult<TargetList, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.datasetTargets(id),
    queryFn: ({ signal }) => api.listTargets(id, signal),
  });
}

/** Record a bar. The direction comes from the metric table, never the body. */
export function useRecordTarget(
  id: string,
): UseMutationResult<Target, Error, TargetCreate> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TargetCreate) => api.recordTarget(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataset(id) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.datasetTargets(id),
      });
    },
  });
}

/** Every attempt against the bar, including the ones that did not clear it. */
export function useEvaluations(
  id: string,
): UseQueryResult<EvaluationList, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.datasetEvaluations(id),
    queryFn: ({ signal }) => api.listEvaluations(id, signal),
  });
}

/** Record a result somebody measured elsewhere, with the run it came from. */
export function useRecordEvaluation(
  id: string,
): UseMutationResult<Evaluation, Error, EvaluationCreate> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: EvaluationCreate) => api.recordEvaluation(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataset(id) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.datasetEvaluations(id),
      });
    },
  });
}

/** Every read of this client's data: granted and refused alike. */
export function useReads(id: string): UseQueryResult<ReadList, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.datasetReads(id),
    queryFn: ({ signal }) => api.listReads(id, undefined, signal),
  });
}

/**
 * Ask for a link, naming why.
 *
 * Nothing is cached: a link is a capability with a clock on it, and a second
 * copy of one in a query cache would outlive the screen that asked for it.
 */
export function useRequestAccess(
  id: string,
  number: number,
): UseMutationResult<AccessGrant, Error, AccessRequest> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AccessRequest) =>
      api.requestAccess(id, number, payload),
    onSuccess: () => {
      // The attempt is now a row in the ledger, granted or refused.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.datasetReads(id),
      });
    },
  });
}

/**
 * The scheduled deletion, or the receipt once one completed.
 *
 * A `404` here is the ordinary answer for a dataset nobody has asked to
 * erase, which is why the screens read `isNotFound` rather than treating it
 * as a failure.
 */
export function useDeletion(
  id: string,
  enabled = true,
): UseQueryResult<DeletionRequest, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.datasetDeletion(id),
    queryFn: ({ signal }) => api.getDeletion(id, signal),
    enabled,
  });
}

/** Ask for the bytes to be destroyed. Asks for your code; 24 hours out. */
export function useRequestDeletion(
  id: string,
): UseMutationResult<DeletionRequest, Error, DeletionRequestCreate> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: DeletionRequestCreate) =>
      api.requestDeletion(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataset(id) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.datasetDeletion(id),
      });
    },
  });
}

/** Cancel inside the window. Keeping a client's file one more day is safe. */
export function useCancelDeletion(
  id: string,
): UseMutationResult<void, Error, void> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.cancelDeletion(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataset(id) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.datasetDeletion(id),
      });
    },
  });
}

// ------------------------------------------------------------------- admin

/** Everybody who has ever signed in, with what they can reach. */
export function useAdminUsers(): UseQueryResult<AdminUserList, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.adminUsers,
    queryFn: ({ signal }) => api.listUsers(signal),
    refetchInterval: pollInterval(ADMIN_POLL_MS, visible),
  });
}

/** Approve an account. Asks for your code: it hands over the whole catalogue. */
export function useApproveUser(): UseMutationResult<
  AdminUser | null,
  Error,
  string
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (username: string) => api.approveUser(username),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
    },
  });
}

/** Turn an account off; every key it holds stops at its next request. */
export function useDisableUser(): UseMutationResult<
  AdminUser | null,
  Error,
  { username: string } & DisableRequest
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ username, ...payload }: { username: string } & DisableRequest) =>
      api.disableUser(username, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
    },
  });
}

/** The audit log, newest first: every consent change and every refused read. */
export function useAudit(): UseQueryResult<AuditList, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.adminAudit(AUDIT_LIMIT),
    queryFn: ({ signal }) => api.getAudit(AUDIT_LIMIT, undefined, signal),
    refetchInterval: pollInterval(ADMIN_POLL_MS, visible),
  });
}

/** Storage health, and the honest one: what could not be read says so. */
export function useStorageHealth(): UseQueryResult<StorageHealth, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.adminStorage,
    queryFn: ({ signal }) => api.getStorageHealth(signal),
    refetchInterval: pollInterval(ADMIN_POLL_MS, visible),
  });
}

/** The emergency stop: no new datasets, uploads or links. It deletes nothing. */
export function useSetPaused(): UseMutationResult<
  PauseState | null,
  Error,
  PauseRequest
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PauseRequest) => api.setPaused(payload),
    onSuccess: () => {
      // Every screen renders the banner from the profile it already holds.
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
      void queryClient.invalidateQueries({ queryKey: queryKeys.config });
    },
  });
}
